/**
 * The dashboard's "needs an admin" queue, ranked by urgency.
 *
 * Pure on purpose: the page feeds it whatever the admin endpoints returned and
 * renders what comes back, so the ranking can be tested without a network.
 *
 * Ranking, in order:
 * 1. Tier. `now` = sellers are losing orders or money right now (failed
 *    integration requests, stores restricted for non-payment, a paid service
 *    that hit its cap). `today` = something is waiting on an admin (wallet
 *    transfers, stores behind on payment, a paid service near its cap).
 *    `later` = housekeeping (area suggestions).
 * 2. Late before on time: an item past its target (a transfer older than
 *    24 hours) rises to the top of its tier.
 * 3. Oldest first.
 */

export type AttentionTier = "now" | "today" | "later";

export type AttentionKind =
  | "failedIntegrations"
  | "restrictedStores"
  | "overdueStores"
  | "walletTransfers"
  | "paidServiceCap"
  | "areaSuggestions";

export type AttentionItem = {
  /** Stable key; one item per kind except paid services (one per service). */
  id: string;
  kind: AttentionKind;
  tier: AttentionTier;
  count: number;
  /** Past its target (e.g. a transfer waiting more than a day). */
  late: boolean;
  /** The moment the oldest piece of this item started waiting (ISO). */
  since: string | null;
  /** Kind-specific facts the page turns into sentences. */
  facts: Record<string, string | number | null>;
};

export type FailedIntegrationsInput = {
  total: number;
  latest: {
    occurredAt: string;
    provider: string;
    storeName: string | null;
    error: string | null;
  } | null;
};

export type OverdueStoresInput = {
  total: number;
  items: Array<{
    storeName: string;
    nextPaymentDate: string | null;
    isRestricted: boolean;
  }>;
};

export type WalletTransfersInput = {
  total: number;
  items: Array<{
    storeName: string;
    amount: number;
    currency: string;
    requestedAt: string;
  }>;
};

export type AreaSuggestionsInput = Array<{
  rawName: string;
  occurrenceCount: number;
  firstSeenAt: string;
}>;

export type PaidServicesInput = Array<{
  service: string;
  nameEn: string;
  nameAr: string;
  percentOfCap: number | null;
  capReachedAt: string | null;
}>;

/** Each source is `undefined` while unknown (loading, failed or not allowed). */
export type AttentionInput = {
  failedIntegrations?: FailedIntegrationsInput;
  overdueStores?: OverdueStoresInput;
  walletTransfers?: WalletTransfersInput;
  areaSuggestions?: AreaSuggestionsInput;
  paidServices?: PaidServicesInput;
};

/** A wallet transfer should be reviewed within a day. */
export const WALLET_TRANSFER_TARGET_HOURS = 24;
/** Same threshold the Paid services page turns amber at. */
export const PAID_SERVICE_WARN_PERCENT = 80;

const TIER_ORDER: Record<AttentionTier, number> = { now: 0, today: 1, later: 2 };
const HOUR = 60 * 60 * 1000;

function oldest(values: Array<string | null | undefined>): string | null {
  let best: string | null = null;
  for (const value of values) {
    if (!value || Number.isNaN(Date.parse(value))) continue;
    if (best === null || Date.parse(value) < Date.parse(best)) best = value;
  }
  return best;
}

export function rankAttention(input: AttentionInput, now: Date): AttentionItem[] {
  const items: AttentionItem[] = [];

  const failed = input.failedIntegrations;
  if (failed && failed.total > 0) {
    items.push({
      id: "failedIntegrations",
      kind: "failedIntegrations",
      tier: "now",
      count: failed.total,
      late: false,
      since: failed.latest?.occurredAt ?? null,
      facts: {
        provider: failed.latest?.provider ?? null,
        storeName: failed.latest?.storeName ?? null,
        error: failed.latest?.error ?? null,
        lastAt: failed.latest?.occurredAt ?? null,
      },
    });
  }

  const overdue = input.overdueStores;
  if (overdue && overdue.total > 0) {
    const restricted = overdue.items.filter((item) => item.isRestricted);
    const pool = restricted.length > 0 ? restricted : overdue.items;
    const since = oldest(pool.map((item) => item.nextPaymentDate));
    const oldestStore =
      pool.find((item) => item.nextPaymentDate === since)?.storeName ??
      pool[0]?.storeName ??
      null;
    items.push(
      restricted.length > 0
        ? {
            id: "restrictedStores",
            kind: "restrictedStores",
            tier: "now",
            count: restricted.length,
            late: true,
            since,
            facts: { oldestStore, overdueTotal: overdue.total },
          }
        : {
            id: "overdueStores",
            kind: "overdueStores",
            tier: "today",
            count: overdue.total,
            late: false,
            since,
            facts: { oldestStore, overdueTotal: overdue.total },
          },
    );
  }

  const wallet = input.walletTransfers;
  if (wallet && wallet.total > 0) {
    const since = oldest(wallet.items.map((item) => item.requestedAt));
    const oldestItem = wallet.items.find((item) => item.requestedAt === since);
    // The sum is only honest when every pending transfer is on this page.
    const complete = wallet.items.length >= wallet.total;
    const currencies = new Set(wallet.items.map((item) => item.currency));
    items.push({
      id: "walletTransfers",
      kind: "walletTransfers",
      tier: "today",
      count: wallet.total,
      late:
        since !== null &&
        now.getTime() - Date.parse(since) > WALLET_TRANSFER_TARGET_HOURS * HOUR,
      since,
      facts: {
        oldestStore: oldestItem?.storeName ?? null,
        totalAmount:
          complete && currencies.size === 1
            ? wallet.items.reduce((sum, item) => sum + item.amount, 0)
            : null,
        currency: currencies.size === 1 ? [...currencies][0] : null,
      },
    });
  }

  for (const service of input.paidServices ?? []) {
    const percent = service.percentOfCap;
    if (percent === null || percent < PAID_SERVICE_WARN_PERCENT) continue;
    const reached = percent >= 100 || service.capReachedAt !== null;
    items.push({
      id: `paidServiceCap:${service.service}`,
      kind: "paidServiceCap",
      tier: reached ? "now" : "today",
      count: 1,
      late: reached,
      since: service.capReachedAt,
      facts: {
        service: service.service,
        nameEn: service.nameEn,
        nameAr: service.nameAr,
        percent: Math.round(percent),
      },
    });
  }

  const suggestions = input.areaSuggestions;
  if (suggestions && suggestions.length > 0) {
    const mostAsked = [...suggestions].sort(
      (a, b) => b.occurrenceCount - a.occurrenceCount,
    )[0];
    items.push({
      id: "areaSuggestions",
      kind: "areaSuggestions",
      tier: "later",
      count: suggestions.length,
      late: false,
      since: oldest(suggestions.map((item) => item.firstSeenAt)),
      facts: {
        mostAsked: mostAsked?.rawName ?? null,
        mostAskedCount: mostAsked?.occurrenceCount ?? null,
      },
    });
  }

  return items.sort((a, b) => {
    if (a.tier !== b.tier) return TIER_ORDER[a.tier] - TIER_ORDER[b.tier];
    if (a.late !== b.late) return a.late ? -1 : 1;
    const aTime = a.since ? Date.parse(a.since) : Number.POSITIVE_INFINITY;
    const bTime = b.since ? Date.parse(b.since) : Number.POSITIVE_INFINITY;
    return aTime - bTime;
  });
}

/** Whole hours or days since `since`, for "Waiting 34 h" / "Waiting 3 days". */
export function elapsed(
  since: string | null,
  now: Date,
): { unit: "minutes" | "hours" | "days"; value: number } | null {
  if (!since) return null;
  const ms = now.getTime() - Date.parse(since);
  if (Number.isNaN(ms) || ms < 0) return null;
  if (ms < HOUR) return { unit: "minutes", value: Math.max(1, Math.floor(ms / 60000)) };
  if (ms < 48 * HOUR) return { unit: "hours", value: Math.floor(ms / HOUR) };
  return { unit: "days", value: Math.floor(ms / (24 * HOUR)) };
}
